// Copyright (c) 2026 Or2 LLC. All rights reserved.
// Insight Platform - proprietary software by Nnaico (https://nnaico.com).
// See the LICENSE file in this repository. All rights reserved.

import { useRef, useEffect, useLayoutEffect, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  List,
  ListItemButton,
  ListItemText,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  Tabs,
  Tab,
  Chip,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ShareIcon from '@mui/icons-material/Share';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import PeopleIcon from '@mui/icons-material/People';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import PushPinIcon from '@mui/icons-material/PushPin';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';

// Module-level: the list's scroll position survives the component being
// unmounted (sidebar collapsed/expanded, mobile drawer closed/reopened).
let savedScrollPosition = 0;

// Module-level variable to persist scroll position during delete operations
// (delete doesn't cause remount, but we need to restore after React re-renders)
let deleteScrollPosition = null;

// Module-level: the Mine/Shared tab survives the component being unmounted
// (sidebar collapsed/expanded, mobile drawer closed/reopened).
let savedActiveTab = 0;

// Format date helper
const formatDate = (timestamp) => {
  if (!timestamp) return '';

  const now = Math.floor(Date.now() / 1000);
  const diffSecs = now - timestamp;
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffSecs / 3600);
  const diffDays = Math.floor(diffSecs / 86400);

  if (diffSecs < 0 || diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} min${diffMins !== 1 ? 's' : ''} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;

  return new Date(timestamp * 1000).toLocaleDateString();
};

// Get thread preview text
const getThreadPreview = (thread) => {
  if (thread.title) {
    return thread.title.length > 50 ? thread.title.slice(0, 50) + '...' : thread.title;
  }
  return 'New conversation';
};

export const ChatHistory = ({
  threads = [],
  sharedThreads = [], // Threads shared with the current user
  currentThreadId,
  onSelectThread,
  onCloseMobile,
  onRenameThread,
  onDeleteThread,
  onLeaveThread, // Callback when user wants to leave a shared thread
  onSearchChats, // Callback to open search modal
  onPinThread, // Callback to pin/unpin a thread
  onShareThread, // Callback to open the share modal for a thread
  onNewChat, // Callback to start a new chat
  customGpts = [], // For showing assistant name next to each thread
}) => {
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuThread, setMenuThread] = useState(null);
  const [activeTab, setActiveTab] = useState(savedActiveTab); // 0 = Mine, 1 = Shared (persisted across remounts)
  const scrollContainerRef = useRef(null);

  // ---- Custom overlay scrollbar -----------------------------------------
  // The native scrollbar of the list is hidden and a thumb is drawn over it:
  // thin at rest, wider while the mouse is over the list or while dragging,
  // draggable, click on the track to jump. Native styling can't do this
  // reliably: Chromium overlay/Fluent scrollbars ignore ::-webkit-scrollbar,
  // and the scrollbar slot itself can't animate its width.
  const THUMB_MIN_PX = 28;
  const listRef = useRef(null);
  const thumbFrame = useRef(0);
  const dragRef = useRef(null); // { startY, startScroll, ratio } while dragging
  const [historyHovered, setHistoryHovered] = useState(false);
  const [thumbDragging, setThumbDragging] = useState(false);
  const [scrollThumb, setScrollThumb] = useState({ top: 0, height: 0, visible: false });
  const thumbWide = historyHovered || thumbDragging;

  const measureThumb = useCallback(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    const { scrollTop, scrollHeight, clientHeight } = el;
    if (scrollHeight <= clientHeight + 1) {
      setScrollThumb((t) => (t.visible ? { top: 0, height: 0, visible: false } : t));
      return;
    }
    const height = Math.max(THUMB_MIN_PX, clientHeight * (clientHeight / scrollHeight));
    const top = (scrollTop / (scrollHeight - clientHeight)) * (clientHeight - height);
    // no re-render when nothing moved
    setScrollThumb((t) => (
      t.visible && Math.abs(t.top - top) < 0.5 && Math.abs(t.height - height) < 0.5
        ? t
        : { top, height, visible: true }
    ));
  }, []);

  // at most one measurement per animation frame, however many events fire
  const scheduleThumb = useCallback(() => {
    if (thumbFrame.current) return;
    thumbFrame.current = requestAnimationFrame(() => {
      thumbFrame.current = 0;
      measureThumb();
    });
  }, [measureThumb]);

  // Re-measure when the viewport OR the content changes size (first load,
  // new/deleted/renamed chats, Mine/Shared tab switch, window resize), not
  // only on scroll.
  useEffect(() => {
    const el = scrollContainerRef.current;
    measureThumb();
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(scheduleThumb);
    ro.observe(el);
    if (listRef.current) ro.observe(listRef.current);
    return () => {
      ro.disconnect();
      if (thumbFrame.current) cancelAnimationFrame(thumbFrame.current);
      thumbFrame.current = 0;
    };
  }, [measureThumb, scheduleThumb]);

  const handleThumbPointerDown = useCallback((e) => {
    const el = scrollContainerRef.current;
    if (!el || e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation(); // a thumb press is not a track click
    const travel = el.clientHeight - scrollThumb.height;
    const maxScroll = el.scrollHeight - el.clientHeight;
    if (travel <= 0 || maxScroll <= 0) return;
    dragRef.current = { startY: e.clientY, startScroll: el.scrollTop, ratio: maxScroll / travel };
    // pointer capture: the drag keeps working when the pointer leaves the list
    e.currentTarget.setPointerCapture(e.pointerId);
    setThumbDragging(true);
  }, [scrollThumb.height]);

  const handleThumbPointerMove = useCallback((e) => {
    const drag = dragRef.current;
    const el = scrollContainerRef.current;
    if (!drag || !el) return;
    el.scrollTop = drag.startScroll + (e.clientY - drag.startY) * drag.ratio;
  }, []);

  const endThumbDrag = useCallback((e) => {
    if (!dragRef.current) return;
    dragRef.current = null;
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      // already released by the browser
    }
    setThumbDragging(false);
  }, []);

  // Click on the track above/below the thumb: scroll so the thumb is
  // centered on the click point.
  const handleTrackPointerDown = useCallback((e) => {
    const el = scrollContainerRef.current;
    if (!el || e.button !== 0) return;
    const travel = el.clientHeight - scrollThumb.height;
    const maxScroll = el.scrollHeight - el.clientHeight;
    if (travel <= 0 || maxScroll <= 0) return;
    const y = e.clientY - e.currentTarget.getBoundingClientRect().top;
    const ratio = Math.min(Math.max((y - scrollThumb.height / 2) / travel, 0), 1);
    el.scrollTo({ top: ratio * maxScroll, behavior: 'smooth' });
  }, [scrollThumb.height]);

  // Lookup assistant name from thread's custom_gpt_id
  const getGptName = (gptId) => {
    if (!gptId) return null;
    const gpt = customGpts.find(g => g.id === gptId);
    return gpt?.name || null;
  };

  // Handle tab change and persist to module-level variable
  const handleTabChange = (e, newValue) => {
    savedActiveTab = newValue;
    setActiveTab(newValue);
  };

  // Determine which threads to display based on active tab
  const displayedThreads = activeTab === 0 ? threads : sharedThreads;

  // Restore the scroll position when the list mounts, save it when it
  // unmounts (layout effect: the DOM node is still there in the cleanup).
  useLayoutEffect(() => {
    const el = scrollContainerRef.current;
    if (el && savedScrollPosition) el.scrollTop = savedScrollPosition;
    return () => {
      savedScrollPosition = el ? el.scrollTop : 0;
    };
  }, []);

  // Restore scroll position after delete (threads change but no remount)
  useEffect(() => {
    if (deleteScrollPosition !== null && scrollContainerRef.current) {
      const targetScroll = deleteScrollPosition;
      const restoreScroll = () => {
        if (scrollContainerRef.current) {
          scrollContainerRef.current.scrollTop = targetScroll;
        }
      };
      // Restore immediately and with multiple attempts
      restoreScroll();
      requestAnimationFrame(restoreScroll);
      setTimeout(restoreScroll, 0);
      setTimeout(() => {
        restoreScroll();
        deleteScrollPosition = null; // Clear after restoration
      }, 50);
    }
  }, [threads.length]); // Run when threads count changes (after delete)

  const handleSelectThread = async (threadId) => {
    // Always call onSelectThread - it will reload if same thread is clicked
    await onSelectThread(threadId);

    // Close mobile sidebar after selection
    if (window.innerWidth <= 768 && onCloseMobile) {
      onCloseMobile();
    }
  };

  const handleMenuOpen = (e, thread) => {
    e.stopPropagation();
    setMenuAnchor(e.currentTarget);
    setMenuThread(thread);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setMenuThread(null);
  };

  const handleRename = async () => {
    if (!menuThread) return;
    const newTitle = prompt('Enter new name:', getThreadPreview(menuThread));
    if (newTitle && newTitle.trim()) {
      await onRenameThread(menuThread.id, newTitle.trim());
    }
    handleMenuClose();
  };

  const handleDelete = async () => {
    if (!menuThread) return;
    // Save scroll position to module-level variable before delete
    // This will be restored by the useEffect when threads.length changes
    deleteScrollPosition = scrollContainerRef.current?.scrollTop || 0;
    await onDeleteThread(menuThread.id);
    handleMenuClose();
  };

  const handleLeave = async () => {
    if (!menuThread || !onLeaveThread) return;
    deleteScrollPosition = scrollContainerRef.current?.scrollTop || 0;
    await onLeaveThread(menuThread.id);
    handleMenuClose();
  };

  const handlePin = async () => {
    if (!menuThread || !onPinThread) return;
    await onPinThread(menuThread.id);
    handleMenuClose();
  };

  const handleShare = () => {
    if (!menuThread || !onShareThread) return;
    onShareThread(menuThread.id);
    handleMenuClose();
  };

  // Check if the menu thread is a shared thread (not owned by user)
  const isSharedThread = menuThread && sharedThreads.some(t => t.id === menuThread.id);

  // Separate pinned and unpinned threads for display with separator
  const pinnedThreads = displayedThreads.filter(t => t.pinned);
  const unpinnedThreads = displayedThreads.filter(t => !t.pinned);

  return (
    <Box
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 150, // Ensure History section always has minimum space
        overflow: 'hidden',
      }}
    >
      {/* Header with Tabs */}
      <Box sx={{ px: 1.5, pt: 1.5, pb: 0.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 0.5, pb: 1 }}>
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
            }}
          >
            History
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
            {onSearchChats && (
              <IconButton
                size="small"
                onClick={onSearchChats}
                sx={{ p: 0.5, color: 'text.secondary' }}
                title="Search Chats"
              >
                <SearchIcon sx={{ fontSize: 20 }} />
              </IconButton>
            )}
          </Box>
        </Box>

        {/* Tabs for Mine / Shared */}
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="fullWidth"
          TabIndicatorProps={{
            style: {
              backgroundColor: activeTab === 1 ? '#f97316' : undefined, // Orange only for Shared tab
            },
          }}
          sx={{
            minHeight: 32,
            '& .MuiTab-root': {
              minHeight: 32,
              py: 0.5,
              fontSize: '0.75rem',
              fontWeight: 600,
              textTransform: 'none',
              color: 'text.secondary',
            },
          }}
        >
          <Tab
            label="Mine"
            sx={{
              '&.Mui-selected': {
                color: 'text.primary',
              },
            }}
          />
          <Tab
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <PeopleIcon sx={{ fontSize: 14, color: activeTab === 1 ? '#f97316' : 'inherit' }} />
                Shared
                {sharedThreads.length > 0 && (
                  <Chip
                    label={sharedThreads.length}
                    size="small"
                    sx={{
                      height: 18,
                      fontSize: '0.65rem',
                      bgcolor: activeTab === 1 ? 'rgba(249, 115, 22, 0.15)' : 'action.hover',
                      color: activeTab === 1 ? '#f97316' : 'text.secondary',
                    }}
                  />
                )}
              </Box>
            }
            sx={{
              '&.Mui-selected': {
                color: '#f97316',
              },
            }}
          />
        </Tabs>
      </Box>

      {/* Scrollable Thread List: native scrollbar hidden, custom overlay
          thumb drawn on top (see "Custom overlay scrollbar" above) */}
      <Box
        sx={{ flex: 1, display: 'flex', minHeight: 0, position: 'relative' }}
        onMouseEnter={() => setHistoryHovered(true)}
        onMouseLeave={() => setHistoryHovered(false)}
      >
      <Box
        ref={scrollContainerRef}
        id="historyScroll"
        onScroll={scheduleThumb}
        sx={{
          flex: 1,
          overflowY: 'auto',
          px: 2,
          py: 1,
          minHeight: 0,
          overflowAnchor: 'none',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
        }}
      >
        <List disablePadding id="historyList" ref={listRef}>
          {displayedThreads.length === 0 ? (
            <Typography
              variant="body2"
              sx={{ textAlign: 'center', color: 'text.disabled', py: 4 }}
            >
              {activeTab === 0 ? 'No conversations yet' : 'No shared conversations'}
            </Typography>
          ) : (
            <>
              {/* Pinned threads */}
              {pinnedThreads.map((thread) => {
                const isInSharedTab = activeTab === 1;
                const isSharedByMe = activeTab === 0 && thread.shared_count > 0;

                return (
                  <ListItemButton
                    key={thread.id}
                    selected={currentThreadId === thread.id}
                    onClick={() => handleSelectThread(thread.id)}
                    dense
                    sx={{
                      // floating rounded highlight, no row dividers: the list
                      // breathes, in tune with the pill-shaped controls
                      borderRadius: 2.5,
                      mb: 0.25,
                      mx: -0.5,
                      py: 0.75,
                      pl: isInSharedTab ? 1.5 : 1,
                      pr: 1,
                      minHeight: 0,
                      position: 'relative',
                      borderLeft: isInSharedTab ? '3px solid #f97316' : '3px solid transparent',
                      '&.Mui-selected': {
                        bgcolor: 'action.selected',
                      },
                      // MUI's default selected+hover is a stronger primary
                      // tint: keep the selected chat the same color under
                      // the pointer
                      '&.Mui-selected:hover': {
                        bgcolor: 'action.selected',
                      },
                    }}
                  >
                    <PushPinIcon sx={{ fontSize: 14, color: 'text.secondary', mr: 0.5, flexShrink: 0 }} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography variant="body2" fontWeight={500} noWrap sx={{ flex: 1 }}>
                          {getThreadPreview(thread)}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={(e) => handleMenuOpen(e, thread)}
                          sx={{ ml: 0.5, p: 0.25, color: 'text.secondary', '&:hover': { bgcolor: 'action.hover' } }}
                        >
                          <MoreHorizIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, whiteSpace: 'nowrap', overflow: 'hidden' }}>
                        {getGptName(thread.custom_gpt_id) ? (
                          <>
                            <Box component="span" sx={{ color: 'text.disabled', fontWeight: 400, fontSize: '0.65rem' }}>
                              {getGptName(thread.custom_gpt_id)}
                            </Box>
                            <Box component="span" sx={{ color: 'text.disabled', fontSize: '0.65rem' }}>·</Box>
                          </>
                        ) : null}
                        <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.disabled' }}>
                          {formatDate(thread.updated_timestamp ?? thread.created_timestamp)}
                        </Typography>
                        {isSharedByMe && (
                          <Box sx={{ ml: 'auto', width: 20, display: 'flex', justifyContent: 'center', flexShrink: 0 }}>
                            <PeopleIcon sx={{ fontSize: 12, color: '#f97316' }} />
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </ListItemButton>
                );
              })}

              {/* Unpinned threads */}
              {unpinnedThreads.map((thread) => {
                const isInSharedTab = activeTab === 1;
                const isSharedByMe = activeTab === 0 && thread.shared_count > 0;

                return (
                  <ListItemButton
                    key={thread.id}
                    selected={currentThreadId === thread.id}
                    onClick={() => handleSelectThread(thread.id)}
                    dense
                    sx={{
                      // floating rounded highlight, no row dividers: the list
                      // breathes, in tune with the pill-shaped controls
                      borderRadius: 2.5,
                      mb: 0.25,
                      mx: -0.5,
                      py: 0.75,
                      pl: isInSharedTab ? 1.5 : 1,
                      pr: 1,
                      minHeight: 0,
                      position: 'relative',
                      borderLeft: isInSharedTab ? '3px solid #f97316' : '3px solid transparent',
                      '&.Mui-selected': {
                        bgcolor: 'action.selected',
                      },
                      // MUI's default selected+hover is a stronger primary
                      // tint: keep the selected chat the same color under
                      // the pointer
                      '&.Mui-selected:hover': {
                        bgcolor: 'action.selected',
                      },
                    }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography variant="body2" fontWeight={500} noWrap sx={{ flex: 1 }}>
                          {getThreadPreview(thread)}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={(e) => handleMenuOpen(e, thread)}
                          sx={{ ml: 0.5, p: 0.25, color: 'text.secondary', '&:hover': { bgcolor: 'action.hover' } }}
                        >
                          <MoreHorizIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, whiteSpace: 'nowrap', overflow: 'hidden' }}>
                        {getGptName(thread.custom_gpt_id) ? (
                          <>
                            <Box component="span" sx={{ color: 'text.disabled', fontWeight: 400, fontSize: '0.65rem' }}>
                              {getGptName(thread.custom_gpt_id)}
                            </Box>
                            <Box component="span" sx={{ color: 'text.disabled', fontSize: '0.65rem' }}>·</Box>
                          </>
                        ) : null}
                        <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.disabled' }}>
                          {formatDate(thread.updated_timestamp ?? thread.created_timestamp)}
                        </Typography>
                        {isSharedByMe && (
                          <Box sx={{ ml: 'auto', width: 20, display: 'flex', justifyContent: 'center', flexShrink: 0 }}>
                            <PeopleIcon sx={{ fontSize: 12, color: '#f97316' }} />
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </ListItemButton>
                );
              })}
            </>
          )}
        </List>
      </Box>

      {/* Overlay track (click to jump) + thumb (drag) */}
      {scrollThumb.visible && (
        <Box
          aria-hidden
          onPointerDown={handleTrackPointerDown}
          sx={{ position: 'absolute', top: 0, bottom: 0, right: 0, width: 13, zIndex: 2 }}
        >
          <Box
            onPointerDown={handleThumbPointerDown}
            onPointerMove={handleThumbPointerMove}
            onPointerUp={endThumbDrag}
            onPointerCancel={endThumbDrag}
            sx={(t) => {
              // one color per theme; idle vs active is width + opacity
              const color = t.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.55)' : 'rgba(0, 0, 0, 0.45)';
              const pressed = t.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.75)' : 'rgba(0, 0, 0, 0.6)';
              return {
                position: 'absolute',
                top: `${scrollThumb.top}px`,
                right: 2, // 2px off the edge; still leaves room for the row menu
                height: `${scrollThumb.height}px`,
                // idle (pointer outside the list): hidden; active (pointer
                // over the list or dragging): 11px and fully opaque
                width: thumbWide ? 11 : 3,
                opacity: thumbWide ? 1 : 0,
                borderRadius: '6px', // rounded ends: a soft pill when active (11px)
                backgroundColor: thumbDragging ? pressed : color,
                transition: 'width 0.15s ease, opacity 0.15s ease, background-color 0.15s ease',
                cursor: 'default',
                userSelect: 'none',
                touchAction: 'none',
                '&:hover': { backgroundColor: pressed },
                // terminal theme: the scheme's scrollbar color (follows theme switches)
                'html.terminal &': {
                  backgroundColor: thumbDragging ? 'var(--app-scrollbar-hover)' : 'var(--app-scrollbar)',
                },
                'html.terminal &:hover': { backgroundColor: 'var(--app-scrollbar-hover)' },
              };
            }}
          />
        </Box>
      )}
      </Box>


      {/* Context Menu for Edit/Delete/Leave */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {isSharedThread ? (
          // Shared thread: only show Leave option
          <MenuItem onClick={handleLeave} sx={{ color: 'error.main' }}>
            <ListItemIcon>
              <ExitToAppIcon fontSize="small" sx={{ color: 'error.main' }} />
            </ListItemIcon>
            Leave conversation
          </MenuItem>
        ) : (
          // Owned thread: show Pin, Rename, and Delete
          <>
            {onPinThread && (
              <MenuItem onClick={handlePin}>
                <ListItemIcon>
                  {menuThread?.pinned ? (
                    <PushPinOutlinedIcon fontSize="small" />
                  ) : (
                    <PushPinIcon fontSize="small" />
                  )}
                </ListItemIcon>
                {menuThread?.pinned ? 'Unpin' : 'Pin'}
              </MenuItem>
            )}
            <MenuItem onClick={handleRename}>
              <ListItemIcon>
                <EditIcon fontSize="small" />
              </ListItemIcon>
              Rename
            </MenuItem>
            {onShareThread && (
              <MenuItem onClick={handleShare}>
                <ListItemIcon>
                  <ShareIcon fontSize="small" />
                </ListItemIcon>
                Share
              </MenuItem>
            )}
            <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
              <ListItemIcon>
                <DeleteIcon fontSize="small" sx={{ color: 'error.main' }} />
              </ListItemIcon>
              Delete
            </MenuItem>
          </>
        )}
      </Menu>
    </Box>
  );
};
